/**
 * Pure, unit-tested logic behind the OfferEditorPage pipeline header's
 * stepper: which OPEN stages exist
 * on the visible track, each one's state relative to the offer's current
 * stage, and the reserved WON/LOST outcome pair. Kept out of the page
 * component so the "click stage X, jump straight there" free-movement
 * semantics are testable without
 * mounting React, mirroring the offersBoard.ts/offersFilter.ts idiom.
 *
 * Also home to the datetime-local <-> ISO round-trip helpers the due-date
 * picker needs (native `<input type="datetime-local">`, same idiom as
 * TmMeetingNewPage's `scheduledAt` field) and the chart color
 * decisions for the stepper's step markers (see sequentialStepColor's
 * docblock).
 */
import type { OfferStage } from "../api/offerStages";

/** Slice of OfferStage the stepper's pure functions need. */
export type PipelineStage = Pick<OfferStage, "id" | "name" | "position" | "kind" | "archived">;

export type StepState = "done" | "current" | "upcoming";

export type PipelineStep<T extends PipelineStage = PipelineStage> = { stage: T; state: StepState };

/** OPEN, non-archived stages sorted by position ascending — the stepper's main track. */
export function openSteps<T extends PipelineStage>(stages: T[]): T[] {
  return stages.filter((s) => s.kind === "OPEN" && !s.archived).sort((a, b) => a.position - b.position);
}

/** The single non-archived WON stage, if any (invariant: exactly one exists once seeded; WON is never archived). */
export function wonStage<T extends PipelineStage>(stages: T[]): T | null {
  return stages.find((s) => s.kind === "WON" && !s.archived) ?? null;
}

/** The single non-archived LOST stage, if any (see {@link wonStage}). */
export function lostStage<T extends PipelineStage>(stages: T[]): T | null {
  return stages.find((s) => s.kind === "LOST" && !s.archived) ?? null;
}

/**
 * Step state for every OPEN stage relative to the offer's current stage:
 * - Offer currently on an OPEN stage: earlier positions read "done", that
 * stage reads "current", later ones read "upcoming".
 * - Offer already reached WON/LOST: every OPEN step reads "done" — the deal
 * walked the whole visible pipeline on its way to an outcome.
 * - No current stage resolvable (defensive — stage_id is NOT NULL):
 * every step reads "upcoming".
 */
export function stepStates<T extends PipelineStage>(stages: T[], currentStageId: string | null): PipelineStep<T>[] {
  const current = stages.find((s) => s.id === currentStageId) ?? null;
  return openSteps(stages).map((stage) => {
    let state: StepState;
    if (!current) state = "upcoming";
    else if (current.kind !== "OPEN") state = "done";
    else if (stage.position < current.position) state = "done";
    else if (stage.id === current.id) state = "current";
    else state = "upcoming";
    return { stage, state };
  });
}

// ---- Color decisions ------------------------------------------------------

// Sequential steps for ordered stages come from the --seq-1..8 tokens in
// styles.css (a contrast-checked blue ramp, valid on light and dark
// surfaces). Colour is never the only signal: every step is paired with an
// icon and a label.
const SEQUENTIAL_RAMP = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => `var(--seq-${n})`);

export function sequentialStepColor(index: number, total: number): string {
  if (total <= 1) return SEQUENTIAL_RAMP[Math.floor(SEQUENTIAL_RAMP.length / 2)];
  const t = Math.max(0, Math.min(1, index / (total - 1)));
  const i = Math.round(t * (SEQUENTIAL_RAMP.length - 1));
  return SEQUENTIAL_RAMP[i];
}

/** Reserved status colour for the WON outcome. */
export const STATUS_GOOD = "var(--ok)";
/** Reserved status colour for the LOST outcome. */
export const STATUS_CRITICAL = "var(--err)";

// ---- due-date <-> ISO helpers ---------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, "0");

/** ISO timestamp -> local `datetime-local` input value ("" for null/invalid — clears the field). */
export function isoToLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Local `datetime-local` input value -> ISO timestamp (null for "" or an unparsable value — clears the reminder). */
export function localInputValueToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
